import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useEffect, useRef } from 'react';
import reactManifest from 'react-manifest';
import Chart from './Chart';
import ForgotPassword from './ForgotPassword';
import Login from './Login';
import XplorSpot from './XplorSpot';
import XplorSpotLite from './XplorSpotLite';
import XplorTrader from './XplorTrader';
import { digitalManifest, portalManifest, spotManifest, traderManifest } from './config/manifest';
import {
    digitalTitle,
    docPortalTitle,
    getEnvironment,
    getPlatform,
    spotTitle,
    traderTitle
} from './helpers/environmentHelper';

const platform = getPlatform();
const environment = getEnvironment();

import danish from 'date-fns/locale/da';
import greek from 'date-fns/locale/el';
import australia from 'date-fns/locale/en-AU';
import canada from 'date-fns/locale/en-CA';
import united_kingdom from 'date-fns/locale/en-GB';
import new_zealand from 'date-fns/locale/en-NZ';
import fr_canada from 'date-fns/locale/fr-CA';
import polish from 'date-fns/locale/pl';
import turkish from 'date-fns/locale/tr';
import ukraine from 'date-fns/locale/uk';
import { registerLocale } from 'react-datepicker';
import ResetPassword from './ResetPassword';
import XplorPortal from './XplorPortal';
import PortalBankAccounts from './XplorPortal/PortalBankAccounts';
import PortalDeposit from './XplorPortal/PortalDeposit';
import PortalInstruments from './XplorPortal/PortalInstruments';
import PortalTrades from './XplorPortal/PortalTrades';
import PortalWallets from './XplorPortal/PortalWallets';
import PortalWithdrawal from './XplorPortal/PortalWithdrawal';
import { PageTransition } from './components/layout/PageTransition';
import { useAppSelector } from './state/hooks';
import { selectLanguage } from './state/reducers/languageSlice';

registerLocale('el', greek);
registerLocale('pl', polish);
registerLocale('tr', turkish);
registerLocale('en-AU', australia);
registerLocale('en-GB', united_kingdom);
registerLocale('en-NZ', new_zealand);
registerLocale('en-CA', canada);
registerLocale('fr-CA', fr_canada);
registerLocale('uk', ukraine);
registerLocale('da', danish);

function App() {
    const lang = useAppSelector(selectLanguage);
    const { pathname } = useLocation();

    useEffect(() => {
        if (pathname.includes('portal')) {
            document.title = docPortalTitle;
            reactManifest.update(portalManifest(environment), '#manifest-placeholder');
        } else {
            if (platform.includes('spot') || platform.includes('debug')) {
                document.title = spotTitle;
                reactManifest.update(spotManifest(environment), '#manifest-placeholder');
            } else if (platform.includes('trader')) {
                document.title = traderTitle;
                reactManifest.update(traderManifest(environment), '#manifest-placeholder');
            } else if (platform.includes('digital')) {
                document.title = digitalTitle;
                reactManifest.update(digitalManifest(environment), '#manifest-placeholder');
            }
        }
    }, [pathname]);

    const location = useLocation();
    const prevPath = useRef<string | null>(null);

    useEffect(() => {
        document.documentElement.lang = lang;
        document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }, [lang]);

    useEffect(() => {
        prevPath.current = location.pathname;
    }, [location]);

    return (
        <PageTransition key={location.pathname}>
            <Routes>
                <Route path="/">
                    <Route index element={<XplorSpot prevPath={prevPath.current} />} />
                    <Route path="trader" element={<XplorTrader prevPath={prevPath.current} />} />
                    <Route path="portal">
                        <Route index element={<XplorPortal />} />
                        <Route path="deposits" element={<PortalDeposit />} />
                        <Route path="withdrawals" element={<PortalWithdrawal />} />
                        <Route path="trades" element={<PortalTrades />} />
                        <Route path="instruments" element={<PortalInstruments />} />
                        <Route path="bank-accounts" element={<PortalBankAccounts />} />
                        <Route path="wallets" element={<PortalWallets />} />
                        {/* <Route path="profile" element={<PortalProfile />} /> */}
                    </Route>
                    <Route path="lite">
                        <Route index element={<XplorSpotLite />} />
                    </Route>
                    <Route path="chart" element={<Chart />} />
                    <Route path="login" element={<Login />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="reset-password" element={<ResetPassword />} />
                    <Route path="*" element={<NoMatch />} />
                </Route>
            </Routes>
        </PageTransition>
    );
}

export default App;

const NoMatch = () => {
    const navigate = useNavigate();
    return (
        <div className="flex flex-col gap-4 h-full w-full justify-center items-center bg-brand-background">
            <div className="flex gap-3">
                <div className="text-brand-neutral-400 text-4xl">404</div>
                <div className="text-brand-text text-4xl font-bold">Page not found</div>
            </div>
            <div className="text-brand-neutral-300 text-lg">
                We&apos;re sorry, the page you requested could not be found
            </div>
            <div
                className="bg-brand-red hover:bg-brand-red-light cursor-pointer rounded-md p-2 px-4 text-brand-text mt-4"
                onClick={() => navigate('/')}>
                Go to login page
            </div>
            <div className="text-brand-red-light mt-6">Kindly reach out to support if you need any help.</div>
        </div>
    );
};
